import React, { useCallback, useEffect } from 'react'
import { addAiResults, selectAiResultPollingEnabled } from '../../../../../store/meetingSummary/aiResults'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useLazyGetRoomSessionQuery, useLazyListAiResultsQuery, useLazyListRoomEventsQuery } from '../../../../../store/media/media'
import { usePollingEffect } from '../../common/hooks/usePollingEffect'
import {
  addExecutedAiFeatureEvents,
  clearPollingManuallyExecutedAiFeatureId,
  selectExecutedAiFeaturesPollingEnabled,
  selectNonExecutedPresetsForTemplate,
  selectPollingManuallyExecutedAiFeatureId,
  setExecutionLoading,
} from '../../../../../store/meetingSummary/executedAiFeatures'
import { useParams } from 'react-router-dom'
import { selectRoomSessionPollingEnabled, setRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { useLazyGetAllTasksQuery } from '../../../../../store/meetingSupport/meetingSupport'
import { selectTaskPollingEnabled, setTasks } from '../../../../../store/task/task'
import { useLazyGetAllBusinessEventsQuery } from '../../../../../store/ai/businessEvent'
import { selectBusinessEventPollingEnabled, setBusinessEvents } from '../../../../../store/businessEvent/businessEvent'
import { BusinessEventSourceType } from '../../../../../models/BusinessEvent'

export const MeetingSummaryPoller: React.FC = () => {
  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)

  usePollAiResults(accountId, roomId, sessionId)
  usePollExecutedFeatures(accountId, roomId, sessionId)
  usePollRoomSession(accountId, roomId, sessionId)
  usePollMeetingTasks(accountId, roomId, sessionId)
  usePollMeetingBusinessEvents(accountId, roomId, sessionId)

  return null
}

const usePollAiResults = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetAiResults] = useLazyListAiResultsQuery()

  const aiResultPollingEnabled = useAppSelector(selectAiResultPollingEnabled)

  const getAiResults = useCallback(async () => {
    const { data: aiResults } = await triggerGetAiResults({
      accountId,
      roomId,
      sessionId,
      pageRequest: { page: 0, size: 500 },
    })
    if (!aiResults?.data) {
      return
    }
    dispatch(addAiResults(aiResults.data))
  }, [dispatch, accountId, roomId, sessionId, triggerGetAiResults])

  const { start: startAiResultPolling, stop: stopAiResultPolling } = usePollingEffect(getAiResults, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (aiResultPollingEnabled) {
      startAiResultPolling()
    } else {
      stopAiResultPolling()
    }
  }, [aiResultPollingEnabled, startAiResultPolling, stopAiResultPolling])
}

const usePollExecutedFeatures = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetRoomEvents] = useLazyListRoomEventsQuery()

  const pollingEnabled = useAppSelector(selectExecutedAiFeaturesPollingEnabled)
  const pollingManuallyExecutedAiFeatureId = useAppSelector(selectPollingManuallyExecutedAiFeatureId)
  const nonExecutedPresets = useAppSelector(selectNonExecutedPresetsForTemplate)

  const getExecutedAiFeatureEvents = useCallback(async () => {
    const { data: roomEvents } = await triggerGetRoomEvents({
      accountId,
      roomId,
      sessionId,
      pageRequest: { page: 0, size: 500 },
      types: ['aiFeatureExecutionStarted', 'userAllocationUpdated'],
    })
    if (!roomEvents?.data) {
      return
    }
    dispatch(addExecutedAiFeatureEvents(roomEvents.data))
  }, [triggerGetRoomEvents, accountId, roomId, sessionId, dispatch])

  const { start: startExecutedFeaturesPolling, stop: stopExecutedFeaturesPolling } = usePollingEffect(getExecutedAiFeatureEvents, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (pollingEnabled) {
      startExecutedFeaturesPolling()
    } else {
      stopExecutedFeaturesPolling()
    }
  }, [pollingEnabled, startExecutedFeaturesPolling, stopExecutedFeaturesPolling])

  useEffect(() => {
    if (!pollingManuallyExecutedAiFeatureId) {
      return
    }
    const manuallyExecutedFeatureExecuted = !nonExecutedPresets.some((preset) => preset.aiFeatureId === pollingManuallyExecutedAiFeatureId)
    if (manuallyExecutedFeatureExecuted) {
      dispatch(clearPollingManuallyExecutedAiFeatureId())
      dispatch(setExecutionLoading(false))
    }
  }, [dispatch, nonExecutedPresets, pollingManuallyExecutedAiFeatureId])
}

const usePollRoomSession = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const [triggerGetRoomSession] = useLazyGetRoomSessionQuery()

  const roomSessionPollingEnabled = useAppSelector(selectRoomSessionPollingEnabled)

  const getRoomSession = useCallback(async () => {
    const { data: roomSession } = await triggerGetRoomSession({
      accountId,
      roomId,
      sessionId,
    })
    if (!roomSession?.data) {
      return
    }
    dispatch(setRoomSession(roomSession.data))
  }, [accountId, dispatch, roomId, sessionId, triggerGetRoomSession])

  const { start: startRoomSessionPolling, stop: stopRoomSessionPolling } = usePollingEffect(getRoomSession, [], {
    interval: 10000,
  })

  useEffect(() => {
    if (roomSessionPollingEnabled) {
      startRoomSessionPolling()
    } else {
      stopRoomSessionPolling()
    }
  }, [roomSessionPollingEnabled, startRoomSessionPolling, stopRoomSessionPolling])
}

const usePollMeetingTasks = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const taskPollingEnabled = useAppSelector(selectTaskPollingEnabled)

  const [triggerGetTasks] = useLazyGetAllTasksQuery()

  const getMeetingTasks = useCallback(async () => {
    try {
      const filters = { type: 'meeting', roomId, sessionId }
      const { data: tasks } = await triggerGetTasks({ accountId, filters }).unwrap()
      if (!tasks.length) {
        return
      }
      dispatch(setTasks(tasks))
    } catch (e) {
      console.error(e)
    }
  }, [accountId, dispatch, roomId, sessionId, triggerGetTasks])

  const { start: startTaskPolling, stop: stopTaskPolling } = usePollingEffect(getMeetingTasks, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (taskPollingEnabled) {
      startTaskPolling()
    } else {
      stopTaskPolling()
    }
  }, [taskPollingEnabled, startTaskPolling, stopTaskPolling])
}

const usePollMeetingBusinessEvents = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const businessEventPollingEnabled = useAppSelector(selectBusinessEventPollingEnabled)

  const [triggerGetBusinessEvents] = useLazyGetAllBusinessEventsQuery()

  const fetchBusinessEvents = async () => {
    try {
      const { data: businessEvents } = await triggerGetBusinessEvents({
        accountId,
        sourceType: BusinessEventSourceType.AI_FEATURE_EXECUTION,
        sourceKey: `${roomId}|${sessionId}`,
        partialKeyMatch: true,
      }).unwrap()
      if (!businessEvents.length) {
        return
      }
      dispatch(setBusinessEvents(businessEvents))
    } catch (e) {
      console.error(e)
    }
  }

  const { start: startBusinessEventPolling, stop: stopBusinessEventPolling } = usePollingEffect(fetchBusinessEvents, [], {
    interval: 5000,
  })

  useEffect(() => {
    if (businessEventPollingEnabled) {
      startBusinessEventPolling()
    } else {
      stopBusinessEventPolling()
    }
  }, [businessEventPollingEnabled, startBusinessEventPolling, stopBusinessEventPolling])
}
